import React, { useMemo, useState } from 'react';

import Debug from '@/components/Debug';
import RenderHTML from '@/components/RenderHTML/RenderHTML';
import UserChallengeImage from '@/components/UserChallengeImage';
import { UserChallengeAnswer } from '@/types/user_challenge';
import { ShowMoreButton } from '@/ui';
import DataList, { DataRow } from '@/ui/components/DataList';
import { cn } from '@/ui/lib/utils';
import usFormatDateString from '@/utils/usFormatDateString';

import {
  FREEFORM_ANSWER_RANK,
  GAD7Choices,
  GAD7Questions,
  PHQ9Choices,
  PHQ9Questions
} from './constants';
import type { Props } from './types';

const dataRowClassName = 'pt-1 space-y-0.5';
const answerTextClassName =
  'text-sm leading-relaxed text-uniform-gray-600 dark:text-uniform-gray-400';

// Questions run long enough to push the answer itself off the card, so a label
// shows its first line.
const questionLabel = (title?: string, text?: string) => {
  if (title) return <span className="line-clamp-1">{title}</span>;

  // The clamp goes on RenderHTML's own block, which cannot sit inside a span.
  if (text) return <RenderHTML html={text} className={cn(answerTextClassName, 'line-clamp-1')} />;

  return '';
};

const UserChallengeAnswers = ({
  answers,
  collapsible = false,
  freeformFirst = false,
  hideImages = false
}: Props) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const answerEntries = useMemo(() => {
    let parsed: { [key: string]: UserChallengeAnswer };

    try {
      parsed = JSON.parse(answers);
    } catch {
      return [];
    }

    if (!parsed) return [];

    let entries = Object.entries(parsed);

    if (hideImages) {
      entries = entries.filter(([, answer]) => answer?.type !== 'photo_upload');
    }

    if (!freeformFirst) return entries;

    return entries
      .map((entry, index) => ({ entry, index }))
      .sort(
        (a, b) =>
          (FREEFORM_ANSWER_RANK[a.entry[1]?.type] ?? FREEFORM_ANSWER_RANK.default) -
            (FREEFORM_ANSWER_RANK[b.entry[1]?.type] ?? FREEFORM_ANSWER_RANK.default) ||
          a.index - b.index
      )
      .map(({ entry }) => entry);
  }, [answers, freeformFirst, hideImages]);

  const renderStructuredAnswer = (structuredEntries: [string, UserChallengeAnswer][]) => {
    const questionType = structuredEntries[0][1].type;
    if (questionType === 'gad7' || questionType === 'phq9') {
      const data = structuredEntries[0][1].value.structured_answer;
      const questions = questionType === 'gad7' ? GAD7Questions : PHQ9Questions;
      const choices = questionType === 'gad7' ? GAD7Choices : PHQ9Choices;
      return (
        <DataList>
          {Object.entries(data ?? {})
            .sort(([keyA], [keyB]) => {
              if (keyA === 'sum') return 1;
              if (keyB === 'sum') return -1;
              return 0;
            })
            .map(([key, value]) => (
              <DataRow className={dataRowClassName} key={key} label={questionLabel(questions[key])}>
                {<span>{key === 'sum' ? value : `${value} - ${choices[value]}`}</span>}
              </DataRow>
            ))}
        </DataList>
      );
    }
  };

  const renderAnswer = (answer: [string, UserChallengeAnswer]) => {
    const title = answer[1]?.value?.title;
    const text = answer[1]?.value?.text;

    if (answer[1]?.type == 'photo_upload' && answer[1]?.value?.photo) {
      const photoId = answer[1].value.photo;

      return (
        <DataRow className={dataRowClassName} key={answer[0]} label={questionLabel(title, text)}>
          <UserChallengeImage userChallengeImageId={photoId} />
        </DataRow>
      );
    }

    // if "text_to_read" it may have title and text and needs to display both
    if (answer[1]?.type == 'text_to_read' && title && text) {
      return (
        <DataRow className={dataRowClassName} key={answer[0]} label={questionLabel(title)}>
          <RenderHTML html={text} className={answerTextClassName} />
        </DataRow>
      );
    }

    return (
      <DataRow className={dataRowClassName} key={answer[0]} label={questionLabel(title, text)}>
        {answer[1]?.value?.answer && <span>{answer[1].value.answer}</span>}
        {answer[1]?.value?.input_date && (
          <span>{usFormatDateString(answer[1].value.input_date)}</span>
        )}
        {answer[1]?.value?.input_number != null && <span>{answer[1].value.input_number}</span>}

        {answer[1]?.value?.answers && (
          <ul>
            {answer[1].value.answers.map((ans, idx) => {
              return <li key={`${ans}-${idx}`}>{ans}</li>;
            })}
          </ul>
        )}
      </DataRow>
    );
  };

  const isCollapsed = collapsible && !isExpanded && answerEntries.length > 1;
  const visibleEntries = isCollapsed ? answerEntries.slice(0, 1) : answerEntries;

  const renderContent = () => {
    if (!answerEntries.length) return 'No answers';

    if (answerEntries[0][1].value.structured_answer) {
      return renderStructuredAnswer(answerEntries);
    }

    return (
      <>
        <DataList>{visibleEntries.map(renderAnswer)}</DataList>

        {collapsible && answerEntries.length > 1 && (
          <ShowMoreButton
            isExpanded={isExpanded}
            onToggle={() => setIsExpanded((expanded) => !expanded)}
          />
        )}
      </>
    );
  };

  return (
    <div className={`relative ${answerTextClassName}`}>
      {renderContent()}

      <Debug object={{ answerEntries, answers }} />
    </div>
  );
};

export default React.memo(UserChallengeAnswers);
